import { getSessionToken } from "@/api/client";
import { ApiError } from "@/api/types";

// D365 Business Central is served by the same separate service (ubik-d365) as
// D365 Finance, on its own host — not proxied through the main app's /api origin.
// api.theubik.com is not a provisioned domain (no DNS, no Cloud Run mapping) —
// this is the service's real Cloud Run URL, stable across redeploys.
// Override via VITE_D365_API_BASE_URL for local dev against a local instance.
const D365_API_BASE =
  import.meta.env.VITE_D365_API_BASE_URL || "https://ubik-d365-finance-zz6bdx6djq-el.a.run.app";

async function d365Request<T>(
  path: string,
  options: { method?: "GET" | "POST"; body?: unknown } = {},
): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = getSessionToken();
  if (token) headers["X-Session-Token"] = token;

  const res = await fetch(`${D365_API_BASE}${path}`, {
    method: options.method ?? "GET",
    headers,
    credentials: "include",
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(
      res.status,
      error.detail ?? error.message ?? error.error ?? res.statusText,
    );
  }

  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

export interface D365BcStartOAuthResponse {
  authorize_url: string;
}

// BC's /test returns a single flat result, unlike Finance's per-check `checks` array.
export type D365BcTestOverall =
  | "connected"
  | "needs_reauth"
  | "needs_bc_binding"
  | "metadata_unreachable";

export interface D365BcTestConnectionResult {
  passed: boolean;
  detail: string;
  overall: D365BcTestOverall;
}

export interface D365BusinessCentralIntegration {
  connected: boolean;
  integration_id?: string;
  tenant_id?: string;
  environment_name?: string;
  connection_status?: string;
  last_tested_at?: string;
}

export const d365BusinessCentralApi = {
  // Unlike D365 Finance, BC's OAuth scope isn't tied to a specific environment —
  // the backend discovers every environment the signed-in user can reach (via
  // BC's /environments API) after token exchange, so no tenant/environment input
  // is collected upfront.
  startOAuth: (): Promise<D365BcStartOAuthResponse> =>
    d365Request("/v1/dynamics365bc/oauth/start", {
      method: "POST",
      body: { product: "business_central" },
    }),

  testConnection: (integrationId: string): Promise<D365BcTestConnectionResult> =>
    d365Request(`/v1/dynamics365bc/business-central/${encodeURIComponent(integrationId)}/test`, {
      method: "POST",
    }),

  getIntegration: (): Promise<D365BusinessCentralIntegration> =>
    d365Request("/v1/dynamics365bc/business-central/integrations"),

  // Retires the integration and discards its tokens; the nightly sync
  // stops until the customer connects again (which creates a new row).
  disconnect: (integrationId: string): Promise<D365BcDisconnectResponse> =>
    d365Request(
      `/v1/dynamics365bc/business-central/${encodeURIComponent(integrationId)}/disconnect`,
      { method: "POST" },
    ),
};

export interface D365BcDisconnectResponse {
  disconnected: boolean;
  integration_id: string;
}
